import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import SectionNavPills from '../components/SectionNavPills'
import { trackEvent } from '../lib/analytics'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const FEEDBACK_TYPES = [
  { value: 'Otázka', label: 'Otázka' },
  { value: 'Technický problém', label: 'Technický problém' },
  { value: 'Platba alebo prístup', label: 'Platba alebo prístup' },
  { value: 'Nápad na zlepšenie', label: 'Nápad na zlepšenie' },
  { value: 'Iné', label: 'Iné' },
]

const Feedback = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  const [formData, setFormData] = useState({
    name: '',
    userEmail: '',
    feedbackType: 'Otázka',
    message: '',
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  // Auto-fill email when user loads
  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({ ...prev, userEmail: user.email }))
    }
  }, [user])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    // Backend contract is unchanged: {subject, message, user_email, current_page}
    // We fold the new "Typ správy" + "Meno" fields into `subject` so the
    // support inbox keeps useful context without changing the API.
    const subjectLine = formData.name
      ? `[${formData.feedbackType}] ${formData.name}`
      : `[${formData.feedbackType}]`

    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subjectLine,
          message: formData.message,
          user_email: formData.userEmail || null,
          current_page: location.pathname,
        }),
      })

      if (response.ok) {
        setSubmitted(true)
        trackEvent('feedback_submitted', {
          feedback_type: formData.feedbackType || undefined,
        })
        setFormData({
          name: '',
          userEmail: user?.email || '',
          feedbackType: 'Otázka',
          message: '',
        })
      } else {
        setError('Správu sa nepodarilo odoslať. Skúste to prosím znova.')
      }
    } catch (err) {
      setError('Správu sa nepodarilo odoslať. Skúste to prosím znova.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SectionNavPills />
        <div className="container mx-auto px-4 py-10 md:py-14">
          <div className="max-w-2xl mx-auto">
            <div
              className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 sm:p-10 text-center"
              data-testid="feedback-success"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 text-green-600 inline-flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                Ďakujeme, správa bola odoslaná.
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mb-6 max-w-md mx-auto">
                Veľmi si ceníme vašu spätnú väzbu a snažíme sa neustále zlepšovať.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                  data-testid="feedback-send-another"
                >
                  Odoslať ďalšiu správu
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition"
                >
                  Späť na hlavnú stránku
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SectionNavPills />
      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="max-w-2xl mx-auto">

          {/* Intro card */}
          <header className="mb-6 md:mb-8 text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Spätná väzba
            </h1>
            <p
              className="text-sm sm:text-base text-gray-700 leading-relaxed max-w-xl mx-auto"
              data-testid="feedback-subheadline"
            >
              Našli ste chybu, máte otázku alebo nápad na zlepšenie? Dajte nám vedieť.
            </p>
            <p
              className="text-sm text-gray-500 mt-2 max-w-xl mx-auto"
              data-testid="feedback-support-text"
            >
              Vaša spätná väzba nám pomáha EduMisiu zlepšovať. Ak píšete kvôli prístupu, platbe alebo účtu, uveďte prosím email použitý pri registrácii.
            </p>
          </header>

          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 sm:p-8">
            {error && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm"
                data-testid="feedback-error"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Meno */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Meno
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={80}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ako vás máme oslovovať?"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Email {!user && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="email"
                  id="userEmail"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleChange}
                  required={!user}
                  disabled={!!user}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="vas.email@example.com"
                />
                {user && (
                  <p className="text-xs text-gray-500 mt-1">
                    Email je automaticky vyplnený z vášho profilu.
                  </p>
                )}
              </div>

              {/* Typ správy */}
              <div>
                <label htmlFor="feedbackType" className="block text-sm font-medium text-gray-700 mb-2">
                  Typ správy <span className="text-red-500">*</span>
                </label>
                <select
                  id="feedbackType"
                  name="feedbackType"
                  value={formData.feedbackType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  data-testid="feedback-type-select"
                >
                  {FEEDBACK_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Správa */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Správa <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  maxLength={2000}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Popíšte prosím váš problém alebo návrh…"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length} / 2000 znakov
                </p>
              </div>

              {/* Submit */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="feedback-submit-button"
                >
                  {submitting ? 'Odosiela sa…' : 'Odoslať spätnú väzbu'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
                  data-testid="feedback-cancel-button"
                >
                  Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Feedback
